import type { AnalysisCheck, CheckFinding } from '../types';
import { Button } from './ui';

// 2026-09-19 re-scope: two figures, not five. `operating_expenses`, `noi` and `cap_rate` are gone
// from `CheckedFigure` (backend/src/services/analysisFigures.ts carries the measured reasons),
// and `rent_psf` followed them out the same day for a units mismatch, not a missing column - do
// not re-add a label for any of them without re-adding the figure first.
const LABEL: Record<string, string> = {
  purchase_price: 'Purchase price',
  gross_income: 'Gross income',
};

/**
 * Falls back to the raw figure string when `LABEL` has no entry for it. `readAnalysisCheck`
 * (frontend/src/lib/api.ts) keeps an unrecognised figure rather than dropping it - this is the
 * other half of that fix: `LABEL[f.figure]` alone renders `undefined` for anything outside the
 * two keys above, which read on screen as the literal sentence "undefined disagrees." An
 * unfamiliar word is an acceptable worst case here; the word "undefined" is not.
 */
const label = (figure: string) => LABEL[figure] ?? figure;

/**
 * In words, never a code. A reason a human cannot read is a reason nobody acts on.
 * `source-lookup-failed` is worded as a retry, not an absence - a failed lookup is a failure to
 * SEE, never the same fact as "this property has no document" (`no-source-document`).
 *
 * `unrecognised-verdict` is CLIENT-SIDE ONLY (api.ts's `readAnalysisCheck`) - the backend never
 * produces it. It is set when the server sends a `verdict` this app has never seen; restating
 * whatever `reason` rode along with THAT verdict would be the fabricated-absence class this
 * feature exists to prevent, one field over, so it gets its own honest wording rather than
 * reusing `figure-not-stated`.
 */
const WHY: Record<string, string> = {
  'no-source-document': 'this property has no source document to check against',
  'source-lookup-failed': 'the document lookup failed, so this could not be checked - worth retrying',
  'document-unreadable': 'the document is a scan with no readable text',
  /**
   * IMPORTANT 3 (final whole-branch review, 2026-09-19). DISTINCT FROM `document-unreadable` ON
   * PURPOSE - a genuine scan RAN and found no text; this is a read that THREW (a storage or
   * network problem, an Anthropic API outage - which has happened twice in this repo's history).
   * Worded as a retry, never as a claim about the document itself, same reasoning as
   * `source-lookup-failed` above.
   */
  'document-read-failed': 'the document could not be read just now - a temporary failure, worth retrying',
  'figure-not-stated': 'the document does not state this figure',
  'not-in-analysis': 'the analysis does not carry this figure',
  'unrecognised-verdict': 'the server sent a verdict this app does not recognise, so this could not be checked',
};

/**
 * Round 2 finding 5's other half: `WHY[f.reason ?? 'figure-not-stated']` asserted a SPECIFIC,
 * false claim - "the document does not state this figure" - for a row where nothing about the
 * document was established, reachable whenever `reason` is null or a value this map has no
 * entry for (`readAnalysisCheck` keeps an unrecognised reason string rather than throwing it
 * away, same treatment as an unrecognised `figure`). Membership is checked against
 * `KNOWN_REASONS` explicitly, rather than a bare `WHY[key] ?? fallback` lookup, so a reason
 * string chosen to collide with an inherited Object property name (`toString`, `constructor`)
 * cannot return anything but the honest fallback either.
 */
const KNOWN_REASONS = Object.keys(WHY);
const UNKNOWN_REASON = 'no reason was given for why this could not be checked';
const why = (f: CheckFinding): string =>
  f.reason !== null && KNOWN_REASONS.includes(f.reason) ? WHY[f.reason] : UNKNOWN_REASON;

/**
 * IMPORTANT 4 (final whole-branch review, 2026-09-19): every entry in `WHY` above is written to
 * read naturally straight after "could not check." - lowercase-first. Capitalizing at THIS one
 * render call site, rather than rewriting the dictionary, fixes the lowercase "the"/"no" that
 * followed a full stop without touching every string (and the tests pinned to their exact text).
 */
const capitalize = (s: string): string => (s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s);

/**
 * Round 2 finding 6, revisited by the 2026-09-19 re-scope: EVERY figure this checker states is a
 * dollar amount now that `cap_rate` - the one percentage figure - is gone (`rent_psf`, the other
 * dollar figure that used to sit in this list, left the same day for a units mismatch, not a
 * formatting one - see `CheckedFigure` in types/index.ts). An unrecognised figure (kept through
 * `readAnalysisCheck`'s cast rather than dropped) still falls through to `plain` below - no unit
 * was ever established for it, and printing e.g. "$1.2" for a ratio nobody ever said was money
 * would be asserting one anyway. There is deliberately no `percent` formatter left in this file:
 * reintroduce one only alongside a figure that is genuinely a percentage, never as a default for
 * "everything that isn't money".
 */
const MONEY_FIGURES: readonly string[] = ['purchase_price', 'gross_income'];

const money = (n: number) => `$${n.toLocaleString('en-US')}`;
/** Neutral: for a figure outside `MONEY_FIGURES` - no unit was ever established for it. */
const plain = (n: number) => n.toLocaleString('en-US');
/**
 * IMPORTANT 6 (final whole-branch review, 2026-09-19). `api.ts` validates `figure`/`verdict`/
 * `reason` against their known unions but never enforced that a `disagrees`/`noted` finding
 * actually carries a `documentValue` - `compareFigures` (backend) only ever produces those two
 * verdicts once a matching fact was found, so today's server always sends one, but nothing on
 * the wire proves it. `n.toLocaleString()` on a `null` used to take out the whole screen; this
 * renders an honest, generic fallback instead of trusting a cast that used to sit at every call
 * site (`f.documentValue as number`) - the callers below now pass `f.documentValue` and
 * `f.analysisValue` straight through, no cast, and `show` itself never crashes on either.
 */
const show = (f: CheckFinding, n: number | null): string =>
  n === null ? 'an unspecified amount' : (MONEY_FIGURES.includes(f.figure) ? money(n) : plain(n));

/**
 * IMPORTANT 4 (final whole-branch review, 2026-09-19). The panel used to say nothing about its
 * own SCOPE - a reader could see the summary line and reasonably assume it covered the analysis,
 * when the exported PDF also prints NOI, cap rate, operating expenses and every derived metric
 * (IRR, equity multiple, DSCR, price/sqft...) this checker never touches (design doc, spec 4:
 * they are all computed FROM the two figures actually checked, so a disagreement in an input
 * already shows up here - checking the derived figures too would double-count one defect as two
 * findings). Named here rather than derived from `CheckedFigure`, matching how `LABEL`/
 * `MONEY_FIGURES`/`WHY` above are hand-maintained rather than computed.
 * ⛔ Update this sentence in the SAME change that adds or removes a figure from `CheckedFigure`
 * (backend `analysisFigures.ts`, mirrored in `types/index.ts`) - a stale scope claim is exactly
 * the fabricated-absence class this feature exists to prevent, one level up from a single figure.
 */
const SCOPE_NOTE =
  'This checks purchase price and gross income only. NOI, cap rate, operating expenses and every ' +
  'other figure or metric on the exported PDF are not checked here.';

/**
 * CRITICAL 1 (final whole-branch review, 2026-09-19). Distinguishes the wording for the two
 * `figuresFrom` states - see `FiguresFrom` (types/index.ts) for why this exists: autosave is a
 * 2-second debounce, so the panel must say plainly whether it judged what is on screen right now
 * or the last saved version, never leave the reader to assume the freshest.
 */
const FIGURES_FROM_NOTE: Record<'screen' | 'saved', string> = {
  screen: 'Compared against the figures on this screen right now.',
  saved: 'Compared against the last saved version of this analysis - not necessarily what is on screen right now.',
};

export interface AnalysisCheckPanelProps {
  check: AnalysisCheck;
  onProceed: () => void;
  /**
   * Review finding 3: the only control used to be "Continue to PDF" - someone who saw a
   * disagreement and wanted to go fix it first had to generate the document they no longer
   * trust just to leave this panel. "Does not block" never meant "forces you forward". This
   * closes the panel and returns to the analysis WITHOUT exporting.
   */
  onDismiss: () => void;
}

/**
 * T6 - the panel shown before a PDF export. A read-only report of what the saved analysis and
 * its own source document agree and disagree on.
 *
 * ⛔ IT DOES NOT BLOCK. "Continue to PDF" is always enabled, regardless of what was found below -
 * a guard that cries wolf on the safe path teaches you to click through on the destructive one.
 * `onDismiss` is the way out that is not "generate the PDF".
 *
 * The document's raw value for a disagreeing figure is shown ONLY inside its quote, never
 * restated as a bare number beside it - the quote already carries it in the document's own
 * words, and repeating it as a second, separate figure would be the same fact stated twice for
 * no reason other than habit.
 */
export const AnalysisCheckPanel = ({ check, onProceed, onDismiss }: AnalysisCheckPanelProps) => {
  const disagrees = check.findings.filter((f) => f.verdict === 'disagrees');
  // Change 2 (2026-09-19): a comparison that ran, and differs, on an INFORMATIONAL figure
  // (currently just purchase_price - the owner analyses at his counter, not the ask, so a price
  // difference is expected). Its own bucket: it must never render among the disagreements (it is
  // not one) and must never be folded into `confirmed` below (the values did not match).
  const noted = check.findings.filter((f) => f.verdict === 'noted');
  const unchecked = check.findings.filter((f) => f.verdict === 'could-not-check');
  const confirmed = check.findings.filter((f) => f.verdict === 'confirmed');
  // ⛔⛔ A FAILURE TO SEE IS NOT AN ABSENCE OF FACT. `sourceDocument: null` alone does not say
  // WHY - it is also null when the lookup itself failed, and telling him "no source document"
  // for that case would claim an absence this check never established. The per-finding reason
  // is the only place that distinction survives once the source has resolved to null.
  const lookupFailed = !check.sourceDocument && check.findings.some((f) => f.reason === 'source-lookup-failed');

  return (
    <div className="mb-5 space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
      {/* ⛔⛔ THE SOURCE IS NAMED FIRST. A wrong-document pairing is caught by a human reading this. */}
      <p className="text-sm text-gray-700">
        {check.sourceDocument ? (
          <>Checked against <strong className="font-semibold text-gray-900">{check.sourceDocument.title}</strong></>
        ) : lookupFailed ? (
          <span className="text-amber-700">The document lookup failed, so this could not be checked - worth retrying.</span>
        ) : (
          <span className="italic text-gray-500">No source document on this property - nothing to check.</span>
        )}
      </p>

      {/* CRITICAL 1: names which figures were judged - the screen's own live values, or the last
          saved row - so the reader never has to assume the freshest during the autosave window. */}
      <p className="text-xs text-gray-500">{FIGURES_FROM_NOTE[check.figuresFrom]}</p>

      {/* IMPORTANT 4: the scope disclosure - stated up front, before any finding, so nobody reads
          what follows as coverage this checker does not have. */}
      <p className="text-xs text-gray-500">{SCOPE_NOTE}</p>

      {disagrees.map((f) => (
        <div key={f.figure} className="rounded-lg border border-red-300 bg-red-50 p-3">
          <p className="text-sm font-semibold text-red-800">
            {label(f.figure)} disagrees - this analysis uses {show(f, f.analysisValue)}
            {f.quote ? ', the document says otherwise' : `; the document states ${show(f, f.documentValue)}`}.
          </p>
          {f.quote && (
            <blockquote className="mt-2 border-l-2 border-red-300 pl-3 text-sm italic text-red-900">
              &quot;{f.quote}&quot;{f.page ? ` (page ${f.page})` : ''}
            </blockquote>
          )}
        </div>
      ))}

      {/* ⛔ NEUTRAL ON PURPOSE - no red, no alarm language. `noted` means the comparison ran and
          the two sides differ, on a figure where that is EXPECTED (Change 2), not a defect. Its
          own section: not among the disagreements above, and not counted as confirmed below. */}
      {noted.map((f) => (
        <div key={f.figure} className="rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-700">
          <p>
            {label(f.figure)}: the document states {show(f, f.documentValue)}; this analysis uses {show(f, f.analysisValue)}.
          </p>
          {f.quote && (
            <blockquote className="mt-2 border-l-2 border-gray-300 pl-3 text-sm italic text-gray-600">
              &quot;{f.quote}&quot;{f.page ? ` (page ${f.page})` : ''}
            </blockquote>
          )}
        </div>
      ))}

      {/* ⛔ Its own section, as prominent as a disagreement. Never folded into the confirmed count. */}
      {unchecked.map((f) => (
        <div key={f.figure} className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span className="font-semibold text-amber-800">{label(f.figure)}: could not check.</span>{' '}
          {/* IMPORTANT 4: capitalized - `why(f)` reads lowercase-first, and it followed a full stop
              here uncapitalized, e.g. "could not check. the document does not state this figure." */}
          {capitalize(why(f))}.
        </div>
      ))}

      {/* ⛔⛔ HONEST ABOUT ALL FOUR STATES, not just "confirmed of total" - that phrasing predates
          `noted` and would otherwise silently fold it into "not confirmed" with no visible count
          of its own, the same fabricated-absence class this feature exists to prevent.
          IMPORTANT 4: "examined", not "checked" - this line used to say "N figures checked in
          total" in the SAME sentence that reports some of them explicitly could NOT be checked,
          claiming to have checked something it just said it did not. */}
      <p className="text-sm text-gray-600">
        {confirmed.length} confirmed, {noted.length} noted, {disagrees.length} disagreeing, {unchecked.length} could not be checked - {check.findings.length} figures examined in total.
      </p>

      <div className="flex justify-end gap-2 pt-1">
        {/* ⛔ THE WAY OUT THAT IS NOT "GENERATE THE PDF" (review finding 3). Closes the panel and
            returns to the analysis without exporting - someone who wants to go fix a
            disagreement first must not have to produce the document they no longer trust just
            to leave this screen. */}
        <Button type="button" variant="outline" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
        {/* ⛔ ALWAYS ENABLED. It reports; it does not block. */}
        <Button type="button" variant="primary" size="sm" onClick={onProceed}>
          Continue to PDF
        </Button>
      </div>
    </div>
  );
};
